import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../service/Api";
import { Loader2, Plus, Edit2, Save, X, Sparkles } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function OnboardingStep1() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", domain: "" });
  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", description: "", domain: "" });
  const [detectedDomain, setDetectedDomain] = useState("");
  const hasLoadedProductsRef = useRef(false);

  useEffect(() => {
    console.log("user", user);
    if (!user || hasLoadedProductsRef.current) {
      return;
    }
    hasLoadedProductsRef.current = true;
    initializeProducts();
  }, [user]);

  const initializeProducts = async () => {
    try {
      setInitializing(true);
      setError(null);

      const domainFromEmail =
        user?.email && user.email.includes("@") ? user.email.split("@")[1] : "";

      if (domainFromEmail) {
        setDetectedDomain(domainFromEmail.toLowerCase());
      }

      const response = await apiService.getTailspinProducts();
      if (response.data.success && Array.isArray(response.data.data)) {
        const existingProducts = response.data.data || [];
        if (existingProducts.length > 0) {
          setProducts(existingProducts);
          return;
        }
      }

      if (!domainFromEmail) {
        setError("We couldn't detect a company domain from your account. You can add products manually below.");
        return;
      }

      setGenerating(true);
      const generateResponse = await apiService.generateProductDescriptions(domainFromEmail);

      if (generateResponse.data.success) {
        setProducts(generateResponse.data.data?.products || []);
      } else {
        setError(generateResponse.data.message || "Failed to generate products automatically.");
      }
    } catch (err) {
      console.error("Error initializing products:", err);
      setError("Failed to initialize products automatically. You can add them manually.");
    } finally {
      setGenerating(false);
      setInitializing(false);
    }
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setEditForm({
      name: product.name,
      description: product.description,
      domain: product.domain
    });
  };

  const handleSaveEdit = async (productId) => {
    try {
      const response = await apiService.updateOnboardingProduct(productId, editForm);

      if (response.data.success) {
        setProducts(products.map(p =>
          p.id === productId ? { ...p, ...editForm } : p
        ));
        setEditingId(null);
      } else {
        setError(response.data.message || "Failed to update product");
      }
    } catch (err) {
      console.error("Error updating product:", err);
      setError("Failed to update product");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: "", description: "", domain: "" });
  };

  const handleCreateNew = async () => {
    if (!newProduct.name.trim() || !newProduct.description.trim()) {
      setError("Name and description are required");
      return;
    }

    try {
      const response = await apiService.createTailspinProduct(newProduct);

      if (response.data.success) {
        setProducts([...products, response.data.data]);
        setNewProduct({ name: "", description: "", domain: "" });
        setShowNewProductForm(false);
      } else {
        setError(response.data.message || "Failed to create product");
      }
    } catch (err) {
      console.error("Error creating product:", err);
      setError("Failed to create product");
    }
  };

  const handleNext = () => {
    if (products.length === 0) {
      setError("Please add at least one product before continuing");
      return;
    }
    navigate("/tailspin/onboarding/step2");
  };

  if (initializing) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
          <p style={{ color: '#0A0A10' }}>
            {generating ? "Extracting your products..." : "Loading your products..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, rgba(255, 107, 90, 0.1), #F8F7F4, rgba(215, 180, 114, 0.1))' }}>
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}>
            <Sparkles className="w-8 h-8" style={{ color: '#FF6B5A' }} />
          </div>
          <h1 className="text-4xl font-bold mb-3" style={{ color: '#0A0A10' }}>
            Welcome to Tailspin
          </h1>
          <p className="text-xl mb-2" style={{ color: '#0A0A10', opacity: 0.7 }}>
            Step 1: Define Your Products
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-semibold" style={{ backgroundColor: '#FF6B5A' }}>
              1
            </div>
            <div className="w-16 h-1" style={{ backgroundColor: '#D7B472', opacity: 0.5 }}></div>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold" style={{ backgroundColor: '#D7B472', opacity: 0.3, color: '#0A0A10' }}>
              2
            </div>
          </div>
        </div>

        {/* Auto Generated Info */}
        <div className="rounded-xl shadow-sm border p-8 mb-8" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
          <h2 className="text-2xl font-semibold mb-3" style={{ color: '#0A0A10' }}>
            Your Tailspin Products
          </h2>
          <p style={{ color: '#0A0A10', opacity: 0.7 }}>
            We detected {detectedDomain ? <span className="font-semibold" style={{ color: '#0A0A10' }}>{detectedDomain}</span> : "your account"}
            {" "}and generated product descriptions automatically. You can edit them below or add new products manually.
          </p>
          {error && (
            <div className="mt-4 p-4 border rounded-lg" style={{ borderColor: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}>
              {error}
            </div>
          )}
        </div>

        {/* Products List */}
        <div className="rounded-xl shadow-sm border p-8 mb-8" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold" style={{ color: '#0A0A10' }}>
              Your Products ({products.length})
            </h2>
            <button
              onClick={() => setShowNewProductForm(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
              style={{ color: '#FF6B5A' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Plus className="w-5 h-5" />
              Add Product
            </button>
          </div>

          {/* New Product Form */}
          {showNewProductForm && (
            <div className="mb-6 p-6 rounded-lg border" style={{ backgroundColor: 'rgba(255, 107, 90, 0.1)', borderColor: '#FF6B5A' }}>
              <h3 className="font-semibold mb-4" style={{ color: '#0A0A10' }}>New Product</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                    placeholder="Enter product name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                    Domain
                  </label>
                  <input
                    type="text"
                    value={newProduct.domain}
                    onChange={(e) => setNewProduct({ ...newProduct, domain: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                    placeholder="e.g., product.company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                    Description *
                  </label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                    placeholder="Describe your product..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleCreateNew}
                    className="px-4 py-2 text-white rounded-lg flex items-center gap-2"
                    style={{ backgroundColor: '#FF6B5A' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                  >
                    <Save className="w-4 h-4" />
                    Create Product
                  </button>
                  <button
                    onClick={() => {
                      setShowNewProductForm(false);
                      setNewProduct({ name: "", description: "", domain: "" });
                    }}
                    className="px-4 py-2 rounded-lg"
                    style={{ backgroundColor: '#D7B472', opacity: 0.3, color: '#0A0A10' }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.5'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Products Grid */}
          {products.length === 0 ? (
            <div className="text-center py-12" style={{ color: '#0A0A10', opacity: 0.7 }}>
              <p className="text-lg mb-2">No products yet</p>
              <p className="text-sm">Generate products or add them manually</p>
            </div>
          ) : (
            <div className="space-y-4">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="p-6 border rounded-lg transition-colors"
                  style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                >
                  {editingId === product.id ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                          Product Name
                        </label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                          style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                          Domain
                        </label>
                        <input
                          type="text"
                          value={editForm.domain}
                          onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                          style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                          Description
                        </label>
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                          style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleSaveEdit(product.id)}
                          className="px-4 py-2 text-white rounded-lg flex items-center gap-2"
                          style={{ backgroundColor: '#FF6B5A' }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                        >
                          <Save className="w-4 h-4" />
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 rounded-lg flex items-center gap-2"
                          style={{ backgroundColor: '#D7B472', opacity: 0.3, color: '#0A0A10' }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.5'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-semibold mb-1" style={{ color: '#0A0A10' }}>
                            {product.name}
                          </h3>
                          {product.domain && (
                            <p className="text-sm" style={{ color: '#FF6B5A' }}>{product.domain}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleEdit(product)}
                          className="p-2 rounded-lg transition-colors"
                          style={{ color: '#0A0A10' }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.1)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                      </div>
                      <p className="leading-relaxed" style={{ color: '#0A0A10', opacity: 0.7 }}>
                        {product.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-end">
          <button
            onClick={handleNext}
            disabled={products.length === 0}
            className="px-8 py-3 text-white rounded-lg disabled:cursor-not-allowed font-medium text-lg transition-colors"
            style={{ backgroundColor: products.length === 0 ? '#D7B472' : '#FF6B5A', opacity: products.length === 0 ? 0.5 : 1 }}
            onMouseEnter={(e) => { if (products.length > 0) e.currentTarget.style.backgroundColor = '#e55a4a'; }}
            onMouseLeave={(e) => { if (products.length > 0) e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
          >
            Continue to Assets →
          </button>
        </div>
      </div>
    </div>
  );
}

